<script>
	import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';

	export let options = [];
	export let value = '';
	export let name = '';
	export let className = '--light';
	export let disabled = false;
	export let emptyLabel = 'Нет данных';

	const dispatch = createEventDispatcher();

	let selectElement;
	let mounted = false;
	let state = null;
	let previousOptionsHash = '';

	// Реактивно обновляем UI, когда меняется value извне
	$: if (mounted && state && String(value) !== String(state.lastValue)) {
		updateUI(value);
		state.lastValue = String(value);
	}

	// Реактивно перестраиваем опции только когда options действительно изменились
	$: if (mounted && state) {
		const currentHash = JSON.stringify(options);
		if (currentHash !== previousOptionsHash) {
			previousOptionsHash = currentHash;
			rebuildOptions();
		}
	}

	function getJQuery() {
		return window.jQuery || window.$ || null;
	}

	function iconAdd(item) {
		if (!item) return '';
		const isJq = typeof item.attr === 'function';
		const dataIc = isJq ? item.attr('data-ic') : item.dataset?.ic;
		const text = isJq ? item.text() : item.textContent || item;
		
		if (dataIc) {
			const href = dataIc.startsWith('#') ? `img/ic.svg${dataIc}` : `img/ic.svg#${dataIc}`;
			return `<svg class="ic"><use href="${href}"></use></svg> ${text}`;
		}
		return text;
	}

	function ensureState() {
		const jq = getJQuery();
		if (!jq || !selectElement) return null;
		if (state) return state;

		const selectJq = jq(selectElement);
		
		// Оборачиваем, если нужно
		if (!selectJq.parent('.select-wrap').length) {
			selectJq.hide().wrap('<div class="select-wrap"></div>');
			if (selectJq.attr('class')) {
				selectJq.parent().addClass(selectJq.attr('class'));
			}
		}

		// Создаем кастомные элементы
		let customSelect = selectJq.next('.select');
		if (!customSelect.length) {
			customSelect = jq(`<div class="select ${className}"></div>`)
				.insertAfter(selectJq)
				.html(`<span class="select-text"></span> <svg class="ic"><use href="img/ic.svg#chevron-down"></use></svg>`);
		} else {
			customSelect.attr('class', `select ${className}`);
		}

		let optionList = customSelect.next('.select-options');
		if (!optionList.length) {
			optionList = jq('<ul class="select-options"></ul>').insertAfter(customSelect);
		}

		// События
		customSelect.off('click.widgetSelect').on('click.widgetSelect', function (e) {
			if (disabled) return;
			e.stopPropagation();
			jq('.select-wrap .select.--active').not(this).removeClass('--active').next('.select-options').slideUp(200);
			jq(this).toggleClass('--active').next('.select-options').slideToggle(200);
		});

		optionList.off('click.widgetSelect').on('click.widgetSelect', 'li', function (e) {
			e.stopPropagation();
			const val = jq(this).attr('rel');
			
			// Обновляем значение через bind:value
			value = val;
			
			// Триггерим нативное событие для совместимости
			selectJq.val(val).trigger('change');
			
			// Диспатчим событие Svelte
			dispatch('change', val);
			
			// Обновляем визуал
			updateUI(val);
			
			// Закрываем
			customSelect.removeClass('--active');
			optionList.slideUp(200);
		});

		const closeHandler = (e) => {
			if (!jq(e.target).closest('.select-wrap').length) {
				customSelect.removeClass('--active');
				optionList.slideUp(200);
			}
		};
		jq(document).off('click.widgetSelectClose').on('click.widgetSelectClose', closeHandler);

		state = { jq, selectJq, customSelect, optionList, closeHandler, lastValue: null };
		return state;
	}

	function updateUI(val) {
		const s = ensureState();
		if (!s) return;
		
		const selectedOption = s.selectJq.find(`option[value="${val}"]`);
		const text = selectedOption.length ? iconAdd(selectedOption.first()) : (emptyLabel || '');
		
		s.customSelect.find('.select-text').html(text);
		
		s.optionList.find('li').removeAttr('selected');
		if (val) {
			s.optionList.find(`li[rel="${val}"]`).attr('selected', 'selected');
		}
		
		s.lastValue = String(val);
	}

	function rebuildOptions() {
		const s = ensureState();
		if (!s) return;

		s.optionList.empty();
		
		const opts = options.length ? options : [{ value: '', label: emptyLabel, disabled: true }];
		
		const html = opts.map(opt => {
			let content = opt.label;
			if (opt.dataIc) {
				const href = opt.dataIc.startsWith('#') ? `img/ic.svg${opt.dataIc}` : `img/ic.svg#${opt.dataIc}`;
				content = `<svg class="ic"><use href="${href}"></use></svg> ${content}`;
			}
			
			const disabledClass = opt.disabled ? ' disabled' : '';
			return `<li rel="${opt.value}" class="${disabledClass}">${content}</li>`;
		}).join('');
		
		s.optionList.html(html);
		updateUI(value);
	}

	function handleNativeChange(event) {
		const newVal = event.currentTarget.value;
		// Не диспатчим здесь, чтобы избежать дублирования
		// Событие уже было отправлено из click обработчика
	}

	onMount(() => {
		mounted = true;
		tick().then(() => {
			ensureState();
			rebuildOptions();
		});
	});

	onDestroy(() => {
		if (state) {
			state.jq(document).off('click.widgetSelectClose', state.closeHandler);
		}
	});
</script>

<select 
	bind:this={selectElement} 
	{name} 
	{disabled} 
	bind:value 
	on:change={handleNativeChange}
	style="display: none;"
>
	{#each options as option}
		<option value={option.value} disabled={option.disabled}>
			{option.label}
		</option>
	{/each}
</select>